import React, { useState, useEffect, useCallback } from 'react';
import { Add } from '@mui/icons-material';
import {
  Typography,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Header from '../Header';

// Import our chunked components
import { ActionButton, StyledTabs } from './StyledComponents';
import { lookupService, getIdField } from './lookupService';
import DataTable from './DataTable';
import EditModal from './EditModal';
import { sections } from './sectionConfig';

// Main QuizLookupMaster Component
const QuizLookupMaster = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [editModal, setEditModal] = useState({ open: false, item: null, type: null });
  const [loading, setLoading] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, id: null });
  const { enqueueSnackbar } = useSnackbar();

  // State for lookup data
  const [lookupData, setLookupData] = useState({
    difficulty: [],
    'question-type': [],
    'answer-type': [],
    'question-mood': [],
    'question-topic': [],
    'question-subtopic': [],
    'question-bank': [],
    'games': [],
    'games-datestamps': []
  });

 
  // Event handlers
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const handleCreateClick = (type) => {
    setEditModal({ open: true, item: 'CREATE', type });
  };

  const handleEditClick = (type, item) => {
    setEditModal({ open: true, item, type });
  };

  const handleModalClose = () => {
    // console.log('Closing modal...');
    setEditModal({ open: false, item: null, type: null });
  };

  const handleModalSave = async (data) => {
    try {
      const { type, item } = editModal;
      // console.log('Saving data:', { type, item, data });

      if (item === 'CREATE') {
        // console.log('Creating new item...');
        await lookupService.createItem(type, data);
        enqueueSnackbar(`${type} created successfully!`, { variant: 'success' });
      } else {
        const idField = getIdField(type);
        // console.log('Updating item with ID:', item[idField]);
        await lookupService.updateItem(type, item[idField], data);
        enqueueSnackbar(`${type} updated successfully!`, { variant: 'success' });
      }

      await loadData(type);
    } catch (error) {
      console.error('Error saving:', error);
      enqueueSnackbar('Error saving data. Please try again.', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      const { type, id } = deleteConfirm;
      await lookupService.deleteItem(type, id);
      enqueueSnackbar(`${type} deleted successfully!`, { variant: 'success' });
      await loadData(type);
      setDeleteConfirm({ open: false, type: null, id: null });
    } catch (error) {
      console.error('Error deleting:', error);
      enqueueSnackbar('Error deleting data. Please try again.', { variant: 'error' });
    }
  };



  const loadData = useCallback(async (type) => {
    try {
      // console.log(`Loading data for ${type}...`);
      setLoading(prev => ({ ...prev, [type]: true }));
      const data = await lookupService.fetchItems(type);
      // console.log(`Loaded ${type} data:`, data);
      setLookupData(prev => ({ ...prev, [type]: data }));
    } catch (error) {
      console.error(`Error loading ${type}:`, error);
      enqueueSnackbar(`Error loading ${type} data.`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  }, [enqueueSnackbar]);

  const loadAllData = useCallback(async () => {
    try {
      const types = ['difficulty', 'question-type', 'answer-type', 'question-mood', 'question-topic', 'question-subtopic', 'question-bank', 'games', 'games-datestamps'];
      const promises = types.map(async (type) => {
        setLoading(prev => ({ ...prev, [type]: true }));
        const data = await lookupService.fetchItems(type);
        setLoading(prev => ({ ...prev, [type]: false }));
        return { type, data };
      });

      const results = await Promise.all(promises);
      const newData = {};
      results.forEach(({ type, data }) => {
        newData[type] = data;
      });

      setLookupData(newData);
    } catch (error) {
      console.error('Error loading data:', error);
      enqueueSnackbar('Error loading data. Please refresh the page.', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const currentSection = sections[activeTab];
  const isLoading = loading[currentSection.key];
  const sectionData = lookupData[currentSection.key] || [];

  return (
    <>
      <Header />
      <Box sx={{ maxWidth: 1200, margin: '0 auto', px: 3, minHeight: '100vh', backgroundColor: '#121212' }}>
        {/* <Card sx={{ mb: 3 }}>
          <CardContent sx={{ backgroundColor: '#121212', pt: 3, pl: 0 }}>
            <Typography variant="h4" gutterBottom>
              Quiz Lookup Management
            </Typography>
            <Divider color="white" />
          </CardContent>
        </Card> */}

        {/* Tab Navigation */}
        <StyledTabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
          {sections.map((section, index) => (
            <Tab
              key={section.key}
              label={section.tabLabel}
              icon={isLoading && activeTab === index ? <CircularProgress size={16} /> : null}
              iconPosition="end"
            />
          ))}
        </StyledTabs>

        {/* Tab Content */}
        <Box
          sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: '8px',
            p: 3,
            mb: 3,
            border: '1px solid #fff',
          }}
        >
          {/* Section Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>
              {currentSection.title}
              {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Typography>
          </Box>

          {/* Create Button and Record Count */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="white">
              Current Records ({sectionData.length})
            </Typography>
            <ActionButton
              variant="contained"
              onClick={() => handleCreateClick(currentSection.key)}
              startIcon={<Add />}
              sx={{
                backgroundColor: '#4CAF50',
                '&:hover': { backgroundColor: '#45a049' }
              }}
            >
              Create {currentSection.title.replace(' Management', '').replace('s', '')}
            </ActionButton>
          </Box>

          {/* Data Table */}
          <DataTable
            headers={currentSection.headers}
            data={sectionData}
            onEdit={(item) => handleEditClick(currentSection.key, item)}
            onDelete={(id) => setDeleteConfirm({ open: true, type: currentSection.key, id })}
            tableType={currentSection.key}
            isLoading={isLoading}
          />
        </Box>

        {/* Edit Modal */}
        <EditModal
          open={editModal.open}
          onClose={handleModalClose}
          editingItem={editModal.item}
          lookupType={editModal.type}
          onSave={handleModalSave}
          enqueueSnackbar={enqueueSnackbar}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, type: null, id: null })}
          PaperProps={{
            sx: {
              backgroundColor: '#1A1A1A',
              border: '1px solid #333',
              borderRadius: '12px',
            }
          }}
        >
          <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ color: '#fff', pt: 2 }}>
            <Typography>
              Are you sure you want to delete this {deleteConfirm.type}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <ActionButton
              variant="outlined"
              onClick={() => setDeleteConfirm({ open: false, type: null, id: null })}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="contained"
              onClick={handleDelete}
              sx={{
                backgroundColor: '#f44336',
                '&:hover': { backgroundColor: '#d32f2f' }
              }}
            >
              Delete
            </ActionButton>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default QuizLookupMaster;